import { useEffect, useState } from 'react'
import { cmsApi } from '../lib/api'
import { cardImagePosition, eventDates, eventPlaces, formatWeekday, statusLabel } from '../lib/format'
import type { BootstrapData, DashboardCounts, DashboardData, EventListItem, PopularSection, ScreenPreset } from '../types'
import type { CmsScreen } from './AppShell'
import { LoadingState } from './LoadingState'

type Props = {
  bootstrap: BootstrapData
  onOpenEvent: (id: string) => void
  onViewAll: () => void
  onNavigate: (target: CmsScreen, preset?: ScreenPreset) => void
}

const SECTIONS: Record<PopularSection, string> = { top: 'Top 3', film: 'Vale o ingresso', profiles: 'Para você seguir' }

const PERIODS: { key: keyof DashboardCounts; label: string }[] = [
  { key: 'today', label: 'Hoje' },
  { key: 'yesterday', label: 'Ontem' },
  { key: 'week', label: 'Últimos 7 dias' },
  { key: 'month', label: 'Últimos 30 dias' },
]

const plural = (count: number, one: string, many: string) => `${count} ${count === 1 ? one : many}`

// How long an import has waited: "hoje", "há 1 dia", "há 3 dias".
const waited = (value: string) => {
  const days = Math.floor((Date.now() - new Date(value).getTime()) / 86_400_000)
  return days < 1 ? 'hoje' : `há ${plural(days, 'dia', 'dias')}`
}

const PICK_STATUS = {
  ended: () => 'Encerrado',
  no_photo: () => 'Sem foto',
  ending: (lastSession: string | null) => lastSession ? `Última sessão: ${formatWeekday(lastSession)}` : 'Acabando',
} as const

// What the team should look at first, the catalog in numbers and, for the
// owner, each person's work (PRD.md, item 101), as Shopify's home opens.
export function Dashboard({ bootstrap, onOpenEvent, onViewAll, onNavigate }: Props) {
  const [recent, setRecent] = useState<EventListItem[]>([])
  const [loading, setLoading] = useState(true)
  const [data, setData] = useState<DashboardData | null>(null)
  const [error, setError] = useState('')
  const [period, setPeriod] = useState<keyof DashboardCounts>('week')
  const canEdit = bootstrap.user.role !== 'viewer'

  useEffect(() => {
    const params = new URLSearchParams({ page: '1', pageSize: '6', status: 'all' })
    cmsApi.listEvents(params)
      .then((result) => setRecent(result.items))
      .finally(() => setLoading(false))
    cmsApi.dashboard()
      .then(setData)
      .catch((requestError) => setError(requestError instanceof Error ? requestError.message : 'Não foi possível carregar a visão geral.'))
  }, [])

  const attention = data?.attention
  const popular = data?.popular
  const pending = Boolean(attention && (
    (popular && (popular.picks.length || popular.cities.length))
    || attention.review.count || attention.no_photo.count || attention.places_without_profile || attention.places_unchecked
  ))

  return (
    <div className="page-stack">
      <section className="page-heading">
        <div>
          <h1>Visão geral</h1>
          <p>O que precisa de atenção, o catálogo em números e as últimas alterações.</p>
        </div>
      </section>

      {error && <div className="notice">{error}</div>}

      <section className="panel">
        <div className="panel__heading">
          <div><h2>Precisa de atenção</h2><p>Só o que pede uma ação agora.</p></div>
        </div>
        {!data ? (!error && <LoadingState />) : !pending ? <div className="empty-inline">Nada pendente.</div> : (
          <div className="attention-list">
            {popular?.picks.map((pick) => (
              <article className="attention-item" key={`${pick.section}-${pick.city}-${pick.id}`}>
                <div className="attention-item__main">
                  <strong>{pick.title}</strong>
                  <small>Popular · {SECTIONS[pick.section]} · {pick.city ?? 'Todas as cidades'} · {PICK_STATUS[pick.status](pick.last_session)}</small>
                </div>
                <button className="button button--secondary" onClick={() => onNavigate('popular', pick.city ? { popularCity: pick.city } : undefined)}>Trocar</button>
              </article>
            ))}
            {popular?.cities.map((item) => (
              <article className="attention-item" key={item.city}>
                <div className="attention-item__main">
                  <strong>{item.city} sem Popular completa</strong>
                  <small>Falta {item.missing.map((section) => SECTIONS[section]).join(' e ')}; o app mostra a cidade mais perto.</small>
                </div>
                <button className="button button--secondary" onClick={() => onNavigate('popular', { popularCity: item.city })}>Abrir Popular</button>
              </article>
            ))}
            {attention && attention.review.count > 0 && (
              <article className="attention-item">
                <div className="attention-item__main">
                  <strong>{plural(attention.review.count, 'importado para revisar', 'importados para revisar')}</strong>
                  {attention.review.oldest && <small>O mais antigo chegou {waited(attention.review.oldest)}.</small>}
                </div>
                {canEdit && <button className="button button--secondary" onClick={() => onNavigate('review')}>Revisar</button>}
              </article>
            )}
            {attention && attention.no_photo.count > 0 && (
              <article className="attention-item attention-item--stacked">
                <div className="attention-item__main">
                  <strong>{plural(attention.no_photo.count, 'evento sem foto', 'eventos sem foto')}</strong>
                  <small>Entre os que ainda acontecem, do mais próximo ao mais distante.</small>
                </div>
                <div className="attention-item__events">
                  {attention.no_photo.items.map((item) => (
                    <button key={item.id} className="button button--secondary" onClick={() => onOpenEvent(item.id)}>{item.title}</button>
                  ))}
                  {attention.no_photo.count > attention.no_photo.items.length && (
                    <span>e mais {attention.no_photo.count - attention.no_photo.items.length}</span>
                  )}
                </div>
              </article>
            )}
            {attention && attention.places_without_profile > 0 && (
              <article className="attention-item">
                <div className="attention-item__main">
                  <strong>{plural(attention.places_without_profile, 'lugar sem perfil', 'lugares sem perfil')}</strong>
                  <small>Sem a página do local no app.</small>
                </div>
                <button className="button button--secondary" onClick={() => onNavigate('places', { placeProfile: 'missing' })}>Ver lugares</button>
              </article>
            )}
            {attention && attention.places_unchecked > 0 && (
              <article className="attention-item">
                <div className="attention-item__main">
                  <strong>{plural(attention.places_unchecked, 'lugar com o Bom saber por conferir', 'lugares com o Bom saber por conferir')}</strong>
                  <small>Acessibilidade, estacionamento, ambiente e animais.</small>
                </div>
                <button className="button button--secondary" onClick={() => onNavigate('places', { placeReview: 'pending' })}>Conferir</button>
              </article>
            )}
          </div>
        )}
      </section>

      {data && (
        <section className="panel">
          <div className="panel__heading">
            <div><h2>Catálogo</h2><p>Os eventos que ainda acontecem, como o filtro Ativos de Eventos conta.</p></div>
          </div>
          <div className="stat-grid stat-grid--flush">
            <article className="stat-card">
              <span>Em cartaz</span>
              <strong>{data.catalog.upcoming_events.toLocaleString('pt-BR')}</strong>
              <small>Eventos com data hoje ou depois.</small>
            </article>
            <article className="stat-card">
              <span>Novos</span>
              <strong>{data.catalog.new_events.toLocaleString('pt-BR')}</strong>
              <small>Eventos que entraram nos últimos 7 dias.</small>
            </article>
            <article className="stat-card">
              <span>Horários</span>
              <strong>{data.catalog.next_occurrences.toLocaleString('pt-BR')}</strong>
              <small>Sessões e horários nos próximos 7 dias.</small>
            </article>
          </div>
          <div className="dashboard-split">
            <RankList title="Por cidade" items={data.catalog.cities.map(({ city, events }) => ({ label: city, value: events }))} />
            <RankList title="Por categoria" items={data.catalog.categories.map(({ category, events }) => ({ label: category, value: events }))} />
          </div>
        </section>
      )}

      {data?.team && (
        <section className="panel">
          <div className="panel__heading">
            <div><h2>Equipe</h2><p>Eventos de cada pessoa; cada um conta uma vez no período, por mais que seja salvo.</p></div>
            <label className="field field--inline">
              <span>Período</span>
              <select value={period} onChange={(event) => setPeriod(event.target.value as keyof DashboardCounts)}>
                {PERIODS.map(({ key, label }) => <option key={key} value={key}>{label}</option>)}
              </select>
            </label>
          </div>
          <div className="table-wrap">
            <table>
              <thead><tr><th>Pessoa</th><th>Criados no CMS</th><th>Importados revisados</th><th>Editados</th><th /></tr></thead>
              <tbody>
                {data.team.map((person) => (
                  <tr key={person.email}>
                    <td><strong>{person.email}</strong>{person.email === bootstrap.user.email && <small>Você</small>}</td>
                    <td>{person.created[period]}</td>
                    <td>{person.imported[period]}</td>
                    <td>{person.edited[period]}</td>
                    <td><button className="text-button" onClick={() => onNavigate('audit', { auditActor: person.email })}>Histórico</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      <section className="panel">
        <div className="panel__heading">
          <div><h2>Atualizados recentemente</h2><p>Os eventos editados por último, do mais novo ao mais antigo.</p></div>
          <button className="text-button" onClick={onViewAll}>Ver todos</button>
        </div>
        {loading ? <LoadingState /> : (
          <div className="compact-event-list">
            {recent.map((event) => (
              <button key={event.id} className="compact-event" onClick={() => onOpenEvent(event.id)}>
                <span className="compact-event__media">
                  {event.image_url ? <img src={event.image_url} alt="" style={{ objectPosition: cardImagePosition(event) }} /> : event.title.slice(0, 1)}
                </span>
                <span className="compact-event__main">
                  <strong>{event.title}</strong>
                  <small>{event.category} · {eventPlaces(event)}</small>
                </span>
                <span className="compact-event__date">{eventDates(event)}</span>
                <span>{event.status !== 'published' && <span className={`status-pill status-pill--${event.status}`}>{statusLabel(event.status)}</span>}</span>
              </button>
            ))}
            {!recent.length && <div className="empty-inline">Nenhum evento encontrado.</div>}
          </div>
        )}
      </section>
    </div>
  )
}

// The first eight, the rest summed.
function RankList({ title, items }: { title: string; items: { label: string; value: number }[] }) {
  const rest = items.slice(8).reduce((sum, item) => sum + item.value, 0)
  return (
    <section className="rank-list" aria-label={title}>
      <h3>{title}</h3>
      <ol>
        {items.slice(0, 8).map((item) => <li key={item.label}><span>{item.label}</span><strong>{item.value.toLocaleString('pt-BR')}</strong></li>)}
        {rest > 0 && <li><span>Outras</span><strong>{rest.toLocaleString('pt-BR')}</strong></li>}
        {!items.length && <li><span>Nenhum evento por vir.</span></li>}
      </ol>
    </section>
  )
}
